/**
 * v2 servicing Slice 8: staff-side thread detail — reply + status/promise
 * decision + archive/unarchive. Implements the two-call orchestration from
 * Slice 6 (§7.3 G7): ONE client_message_id per attempt-series → postMessage
 * → only on confirm → updateStatus. A message/status ambiguous failure
 * offers a same-key retry; a 409 (client_message_id reused with a different
 * body) surfaces an error and never auto-retries with a fresh key; a
 * message-ok/status-failed split offers an update-only retry.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  PortalMessage,
  StaffPortalRequest,
  staffPortalRequestsApi,
} from '../../../services/staffPortalRequestsApi';
import { generateUuid } from '../../../utils/uuid';

interface Props {
  request: StaffPortalRequest;
  onUpdated: (updated: StaffPortalRequest) => void;
  onArchiveToggle: () => void;
}

type Banner =
  | { kind: 'message_failed'; message: string }
  | { kind: 'message_conflict'; message: string }
  | { kind: 'status_failed'; message: string }
  | null;

const StaffRequestThread: React.FC<Props> = ({ request, onUpdated, onArchiveToggle }) => {
  const [messages, setMessages] = useState<PortalMessage[]>(request.messages ?? []);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [status, setStatus] = useState<StaffPortalRequest['status']>(request.status);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const pendingClientMessageId = useRef<string | null>(null);
  const pendingDecision = useRef<'approved' | 'declined' | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    staffPortalRequestsApi.getThread(request.id).then((full) => {
      // ignore a stale response if the thread changed (or a local write
      // already landed) before this fetch resolved
      if (cancelled) return;
      setMessages(full.messages ?? []);
      setHasMoreOlder((full.messages?.length ?? 0) >= 20);
    });
    return () => {
      cancelled = true;
    };
  }, [request.id]);

  const loadOlder = async () => {
    if (messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const older = await staffPortalRequestsApi.getMessages(request.id, messages[0].id, 20);
      setMessages((prev) => [...[...older].reverse(), ...prev]);
      setHasMoreOlder(older.length === 20);
    } finally {
      setLoadingOlder(false);
    }
  };

  const updateStatusOnly = async (targetStatus: StaffPortalRequest['status'] = status) => {
    try {
      const updated = await staffPortalRequestsApi.updateStatus(request.id, {
        status: targetStatus,
        promise_decision: pendingDecision.current,
      });
      pendingDecision.current = undefined;
      setStatus(targetStatus);
      setBanner(null);
      onUpdated(updated);
    } catch {
      setBanner({ kind: 'status_failed', message: 'Reply sent — status not updated.' });
    }
  };

  const save = async (targetStatus: StaffPortalRequest['status'] = status) => {
    setSaving(true);
    setBanner(null);
    try {
      const body = replyBody.trim();
      if (body) {
        const cmid = pendingClientMessageId.current ?? generateUuid();
        pendingClientMessageId.current = cmid;
        try {
          const msg = await staffPortalRequestsApi.postMessage(request.id, body, cmid);
          setMessages((prev) => [...prev, msg]);
          setReplyBody('');
          pendingClientMessageId.current = null;
        } catch (err: any) {
          if (err?.response?.status === 409) {
            setBanner({
              kind: 'message_conflict',
              message:
                'This reply could not be sent as a new message. Please reload and try again.',
            });
          } else {
            setBanner({
              kind: 'message_failed',
              message: 'Unable to send the reply. Please try again.',
            });
          }
          return;
        }
      }
      await updateStatusOnly(targetStatus);
    } finally {
      setSaving(false);
    }
  };

  const decide = (decision: 'approved' | 'declined') => {
    pendingDecision.current = decision;
    save('resolved');
  };

  const toggleArchive = async () => {
    setArchiving(true);
    try {
      if (request.archived_by_staff_at) {
        await staffPortalRequestsApi.unarchive(request.id);
      } else {
        await staffPortalRequestsApi.archive(request.id);
      }
      onArchiveToggle();
    } finally {
      setArchiving(false);
    }
  };

  const authorLabel = (m: PortalMessage) =>
    m.is_backfilled
      ? 'Historical — original author not recorded'
      : m.author_type === 'staff'
        ? 'Staff'
        : 'Borrower';

  return (
    <div className="px-4 py-4 bg-gray-50 dark:bg-gray-900 space-y-3">
      {hasMoreOlder && (
        <button
          type="button"
          onClick={loadOlder}
          disabled={loadingOlder}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          Load older messages
        </button>
      )}
      <ul className="space-y-2">
        {messages.map((m) => (
          <li
            key={m.id}
            className={`rounded-lg px-3 py-2 text-sm ${
              m.is_backfilled ? 'bg-gray-100 text-gray-500 italic' : 'bg-white text-gray-800'
            }`}
          >
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span className="font-medium">{authorLabel(m)}</span>
              <span>{new Date(m.created_at).toLocaleString()}</span>
            </div>
            <p>{m.body}</p>
          </li>
        ))}
      </ul>

      {banner && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
          <span>{banner.message}</span>
          {banner.kind === 'message_failed' && (
            <button
              type="button"
              onClick={() => save()}
              className="text-xs font-semibold text-amber-800 underline shrink-0"
            >
              Retry
            </button>
          )}
          {banner.kind === 'status_failed' && (
            <button
              type="button"
              onClick={() => updateStatusOnly()}
              className="text-xs font-semibold text-amber-800 underline shrink-0"
            >
              Retry status update
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <textarea
          rows={2}
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder="Response to the borrower"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StaffPortalRequest['status'])}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="open">Open</option>
          <option value="in_review">In review</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <button
          type="button"
          onClick={() => save()}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          Save
        </button>
        {request.request_type === 'promise_to_pay' && request.proposed_promise_date && (
          <>
            <button
              type="button"
              onClick={() => decide('approved')}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Approve Promise
            </button>
            <button
              type="button"
              onClick={() => decide('declined')}
              disabled={saving}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Decline Promise
            </button>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={toggleArchive}
        disabled={archiving}
        className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
      >
        {request.archived_by_staff_at ? 'Unarchive' : 'Archive'}
      </button>
    </div>
  );
};

export default StaffRequestThread;
